import { useEffect, useRef, useState, useCallback } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { ExternalLink, Undo2, Send } from 'lucide-react'
import { MessageBubble } from './message-bubble'
import { useConversation, useEditMessage, useSendReply, type Conversation, type Message } from './use-inbox'

interface MessageThreadProps {
  conversation: Conversation | null
  className?: string
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase()
}

interface EditState {
  message: Message
  draft: string
}

interface UndoState {
  message: Message
  expiresAt: number
}

function UndoBar({ undo, onUndo, onDismiss }: { undo: UndoState; onUndo: () => void; onDismiss: () => void }) {
  const [remaining, setRemaining] = useState(
    Math.max(0, Math.ceil((undo.expiresAt - Date.now()) / 1000)),
  )

  useEffect(() => {
    const interval = setInterval(() => {
      const secs = Math.max(0, Math.ceil((undo.expiresAt - Date.now()) / 1000))
      setRemaining(secs)
      if (secs === 0) {
        onDismiss()
        clearInterval(interval)
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [undo.expiresAt, onDismiss])

  return (
    <div className="flex items-center justify-between border-t bg-amber-500/10 border-amber-500/25 px-4 py-2 ">
      <span className="text-xs text-amber-800 dark:text-amber-200">
        Message sent — undo available for{' '}
        <span className="font-semibold tabular-nums">{remaining}s</span>
      </span>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 border-amber-300 text-xs"
          onClick={onUndo}
        >
          <Undo2 className="h-3 w-3" />
          Undo
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={onDismiss}
        >
          Dismiss
        </Button>
      </div>
    </div>
  )
}

export function MessageThread({ conversation, className }: MessageThreadProps) {
  const bottomRef = useRef<HTMLDivElement>(null)
  const [editState, setEditState] = useState<EditState | null>(null)
  const [undoState, setUndoState] = useState<UndoState | null>(null)

  const { data: messages = [], isLoading } = useConversation(
    conversation?.lead_id ?? null,
  )
  const editMutation = useEditMessage()
  const sendReply = useSendReply()
  const [replyDraft, setReplyDraft] = useState('')

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleEditRequest = useCallback((message: Message) => {
    setEditState({ message, draft: message.content })
  }, [])

  const handleSaveEdit = useCallback(async () => {
    if (!editState || !conversation) return
    const saved = editState
    setEditState(null)
    await editMutation.mutateAsync({
      messageId: saved.message.id,
      leadId: conversation.lead_id,
      newContent: saved.draft,
    })
    setUndoState({ message: saved.message, expiresAt: Date.now() + 5 * 60_000 })
  }, [editState, conversation, editMutation])

  const handleUndo = useCallback(() => {
    if (!undoState || !conversation) return
    // Restore original content
    editMutation.mutate({
      messageId: undoState.message.id,
      leadId: conversation.lead_id,
      newContent: undoState.message.content,
    })
    setUndoState(null)
  }, [undoState, conversation, editMutation])

  if (!conversation) {
    return (
      <div className={cn('flex flex-1 items-center justify-center', className)}>
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            Select a conversation to view messages
          </p>
        </div>
      </div>
    )
  }

  const isEscalated = conversation.status === 'escalated'
  const isManuallyHandled = Boolean(conversation.manually_handled_at)

  return (
    <div className={cn('flex flex-1 flex-col overflow-hidden', className)}>
      {/* Lead header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="text-xs">
              {getInitials(conversation.lead_name)}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm font-semibold leading-none">{conversation.lead_name}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{conversation.lead_company}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs" asChild>
          <a href="#" onClick={(e) => e.preventDefault()}>
            <ExternalLink className="h-3 w-3" />
            View Lead
          </a>
        </Button>
      </div>

      {/* Escalation warning */}
      {isEscalated && (
        <div className="border-b bg-red-500/10 border-red-500/25 px-4 py-2 ">
          <p className="text-xs font-medium text-red-300 ">
            Warning: Negative sentiment detected. This conversation has been escalated for
            manual review.
          </p>
        </div>
      )}

      {/* Manual handoff banner — shown once the user has messaged this lead
          (via the inbox OR directly on LinkedIn, which the inbox sync detects). */}
      {isManuallyHandled && (
        <div className="border-b bg-blue-500/10 border-blue-500/25 px-4 py-2 ">
          <p className="text-xs font-medium text-blue-300">
            <span className="font-semibold">You've taken over this conversation.</span>{' '}
            AI auto-messaging is paused — the system won't contact this lead again.
          </p>
        </div>
      )}

      {/* Message list */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {isLoading && (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  'flex',
                  i % 2 === 0 ? 'justify-end' : 'justify-start',
                )}
              >
                <div
                  className={cn(
                    'h-12 w-56 animate-pulse rounded-2xl bg-muted',
                  )}
                />
              </div>
            ))}
          </div>
        )}

        {!isLoading && messages.length === 0 && (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm text-muted-foreground">No messages yet</p>
          </div>
        )}

        {!isLoading && (
          <div className="space-y-4">
            {messages.map((message) => (
              <div key={message.id}>
                {editState?.message.id === message.id ? (
                  /* Inline editor */
                  <div
                    className={cn(
                      'flex flex-col gap-2',
                      message.direction === 'outbound' ? 'items-end' : 'items-start',
                    )}
                  >
                    <span className="text-[10px] text-muted-foreground">
                      Editing message
                    </span>
                    <textarea
                      className="w-full max-w-[75%] rounded-xl border bg-background p-3 text-sm outline-none ring-1 ring-primary focus:ring-2"
                      rows={4}
                      value={editState.draft}
                      onChange={(e) =>
                        setEditState((prev) =>
                          prev ? { ...prev, draft: e.target.value } : null,
                        )
                      }
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="h-7 text-xs"
                        onClick={handleSaveEdit}
                        disabled={editMutation.isPending || !editState.draft.trim()}
                      >
                        {editMutation.isPending ? 'Saving…' : 'Save & Resend'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setEditState(null)}
                        disabled={editMutation.isPending}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <MessageBubble
                    message={message}
                    leadName={conversation.lead_name}
                    onEditRequest={
                      message.editable ? handleEditRequest : undefined
                    }
                  />
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* Undo bar */}
      {undoState && (
        <UndoBar
          undo={undoState}
          onUndo={handleUndo}
          onDismiss={() => setUndoState(null)}
        />
      )}

      {/* Reply composer */}
      <div className="border-t px-4 py-3">
        <div className="flex gap-2">
          <textarea
            className="flex-1 min-h-[40px] max-h-[120px] rounded-lg border bg-background px-3 py-2 text-sm resize-none placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            placeholder="Type a reply..."
            value={replyDraft}
            onChange={(e) => setReplyDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                if (replyDraft.trim() && conversation) {
                  sendReply.mutate(
                    { leadId: conversation.lead_id, content: replyDraft.trim() },
                    { onSuccess: () => setReplyDraft('') },
                  )
                }
              }
            }}
            disabled={sendReply.isPending}
          />
          <Button
            size="sm"
            className="self-end h-[40px] px-3"
            disabled={!replyDraft.trim() || sendReply.isPending || !conversation}
            onClick={() => {
              if (replyDraft.trim() && conversation) {
                sendReply.mutate(
                  { leadId: conversation.lead_id, content: replyDraft.trim() },
                  { onSuccess: () => setReplyDraft('') },
                )
              }
            }}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        {sendReply.error && (
          <p className="mt-1 text-xs text-destructive">
            {sendReply.error instanceof Error ? sendReply.error.message : 'Failed to send'}
          </p>
        )}
      </div>
    </div>
  )
}
